import React, { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, Phone, Mail, ArrowRight, Car, Wrench, FileText, MessageSquare } from 'lucide-react';

const SubmissionSuccess = () => {
  const [searchParams] = useSearchParams();
  const formType = searchParams.get('type') || 'inquiry';

  useEffect(() => {
    window.scrollTo(0, 0);

    // Fire Meta Pixel Lead event on page load (this is the conversion event Meta tracks)
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'Lead', {
        content_name: formType,
        content_category: 'form_submission',
        status: 'submitted'
      });
    }

    // Fire Google Ads conversion
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        send_to: 'AW-17797421129/LrUYCIi_tdMbEMmwvKZC',
        event_category: 'form',
        event_label: formType
      });
    }
  }, [formType]);

  const formTypeLabels = {
    inquiry: 'Vehicle Inquiry',
    contact: 'Message',
    service: 'Service Request',
    lead: 'Information Request',
    'trade_in': 'Trade-In Evaluation',
    'test_drive': 'Test Drive Request',
    financing: 'Financing Request',
    'pre-approval': 'Pre-Approval Application',
    footer: 'Message',
    popup: 'Special Offer Request'
  };

  const label = formTypeLabels[formType] || 'Submission';

  const getIcon = () => {
    switch (formType) {
      case 'service': return <Wrench className="w-8 h-8 text-green-600" />;
      case 'financing':
      case 'pre-approval': return <FileText className="w-8 h-8 text-green-600" />;
      case 'contact':
      case 'footer': return <MessageSquare className="w-8 h-8 text-green-600" />;
      default: return <Car className="w-8 h-8 text-green-600" />;
    }
  };

  return (
    <>
      <Helmet>
        <title>Submission Received - Paradise Performance</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="description" content="Your submission has been received. We'll be in touch shortly." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section */}
        <div className="relative pt-28 pb-16 overflow-hidden">
          {/* Background accents */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-20 left-10 w-72 h-72 bg-green-500 rounded-full blur-3xl" />
            <div className="absolute top-40 right-20 w-96 h-96 bg-blue-500 rounded-full blur-3xl" />
          </div>

          <div className="container mx-auto px-4 relative">
            <div className="max-w-2xl mx-auto text-center">
              {/* Success Animation */}
              <div className="relative inline-block mb-8">
                <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center mx-auto" style={{ animation: 'pulse 2s ease-in-out infinite' }}>
                  <CheckCircle className="w-14 h-14 text-green-600" />
                </div>
                <div
                  className="absolute inset-0 w-24 h-24 rounded-full border-4 border-green-400 mx-auto"
                  style={{
                    animation: 'ping 1.5s cubic-bezier(0, 0, 0.2, 1) 1',
                    opacity: 0,
                  }}
                />
              </div>

              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                We Got Your {label}!
              </h1>

              <p className="text-xl text-gray-600 mb-8">
                Our team will reach out within 24 hours. We can't wait to help you.
              </p>
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="container mx-auto px-4 pb-16">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="bg-gray-900 text-white px-8 py-5 flex items-center gap-3">
                {getIcon()}
                <h2 className="text-xl font-bold">What Happens Next</h2>
              </div>

              <div className="p-8">
                <div className="space-y-6">
                  {/* Step 1 */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-blue-700 font-bold text-sm">1</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 text-lg">We received your {label.toLowerCase()}</h3>
                      <p className="text-gray-600">Your information is in our system. A team member has been notified.</p>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-blue-700 font-bold text-sm">2</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 text-lg">Quick follow-up</h3>
                      <p className="text-gray-600">
                        Expect a call or text from us within 24 hours. Most requests get a response same day.
                      </p>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-blue-700 font-bold text-sm">3</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 text-lg">
                        {formType === 'service' ? 'Appointment confirmed' : 'Get you taken care of'}
                      </h3>
                      <p className="text-gray-600">
                        {formType === 'service'
                          ? "We'll lock in your appointment time and send you a confirmation."
                          : "Whether it's a test drive, financing or just questions — we'll handle everything."
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Card */}
            <div className="mt-8 bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="font-semibold text-gray-900 text-lg mb-4">Need us sooner? Reach out directly.</h3>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="tel:8173822316"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  (817) 382-2316
                </a>
                <a
                  href="mailto:sales@paradise-performance.com"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl font-semibold transition-colors"
                >
                  <Mail className="w-5 h-5" />
                  Email Us
                </a>
              </div>
            </div>

            {/* Back to site */}
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-6">
              <Link
                to="/vehicles"
                className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-semibold transition-colors"
              >
                Browse Vehicles
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/golf-carts"
                className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-semibold transition-colors"
              >
                Browse Golf Carts
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 font-semibold transition-colors"
              >
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SubmissionSuccess;
